/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias, and } from '@ember/object/computed';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Resource from 'linced-app/enums/enrolment-resource';
import State from 'linced-app/enums/enrolment-request-state';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import config from 'linced-app/config/environment';
import moment from 'moment';
import { observes } from '@ember-decorators/object';

export default class NzMoe extends Component {
  // Attrs
  // enrolURI provides the website to send users to when showing a default
  // 'go to ENROL' link.
  enrolURI = 'https://enrol.education.govt.nz';

  // entityType specifies the type of entity this component is working with.
  entityType = 'person';

  // identifier contains the returned ENROL NSN identifier response.
  // Populated after receiving a result from ENROL.
  identifier = null;

  // pollTimer provides an object to periodically poll the enrolment status
  // endpoint while waiting upon the enrolment service receiving snail-mail
  // envelope back from DEX/ENROL.
  @tracked
  pollTimer = null;

  // State provides the enums for the status endpoint's progress on processing
  // a DEX/ENROL response.
  State = State;

  // Services
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  students;

  // Props
  // person contains a student person resource to get/check a National Student
  // Number (NSN) for.
  person = null;

  // isValid informs the component if the incoming NSN stored within the
  // student resource is known to be an invalid NSN.
  isValid = false;

  // State
  // detailDialogOpen provides the dialog opened state for the returned ENROL
  // GetCheckNSN result.
  @tracked
  detailDialogOpen = false;

  // isConfirming provides the state for changing changing to a dialog to
  // confirm the returned ENROL Result(s).
  @tracked
  isConfirming = false;

  // isRejecting provides the state for changing to a dialog to reject the
  // returned ENROL Result(s).
  @tracked
  isRejecting = false;

  // selectedResult contains the result selected to be shown with the
  // differences before merging the details into the student record.
  @tracked
  selectedResult = null;

  // Status contains the Status response as returned from the enrolment service.
  status = null;

  @tracked
  searchResponse = null;

  @alias('person.fields.nzMOE:nsn.value')
  entityFieldValue;

  @tracked
  originalValue = null;
  redirect = true;

  // Validation against MoE/DEX rules to prevent sending "invalid" requests.
  @computed('person.fields.core:dob.value')
  get dobValid() {
    let dob = this.get('person.fields.core:dob.value');
    return !isEmpty(dob) && moment(dob, 'YYYY-MM-DD', true).isValid();
  }

  @computed('person.gender')
  get genderValid() {
    let gender = this.get('person.gender');
    return gender === 'M' || gender === 'F';
  }

  @and('dobValid', 'genderValid')
  requestValid;

  // init provides the component's constructor method.
  init() {
    super.init(...arguments);
    if (this.entityType !== 'application') {
      return this.checkStatus();
    }
    this.originalValue = this.person.jurisdictionalId;
  }

  // Methods
  archiveIdentifier() {
    let url = `${config.APP.apiHost}/enrolments/${config.APP.apiPath}/identifiers/${this.identifier.id}/:archive`;
    return this.fetch.post(url).then(() => {
      if (!(this.isDestroying || this.isDestroyed)) {
        this.set('identifier', null);
        this.set('status', null);
      }
    });
  }

  baseFilterParams() {
    let params = {
      jurisdiction: Jurisdiction.NZ_MOE,
    };
    switch (this.entityType) {
      case 'application':
        params.applicationId = this.person.id;
        break;
      default:
        params.personId = this.person.id;
        break;
    }
    return params;
  }

  checkStatus() {
    let params = this.baseFilterParams();
    return this.store.query('enrolments/identifier', params).then((ids) => {
      if (isEmpty(ids)) {
        // No existing non-archived Identifier records.  Check for in-flight
        // Status requests.
        params.status = State.PROCESSING;
        params.resource = Resource.IDENTIFIERS;
        return this.store
          .query('enrolments/status', params)
          .then((statuses) => {
            if (!isEmpty(statuses)) {
              // We got at least one Status.
              // We should usually only have one in-flight Status request at most,
              // so just pick the first one returned.
              this.set('status', statuses[0]);
            }
          });
      } else {
        // We have an Identifier response.
        this.set('identifier', ids[0]);
      }
    });
  }

  /**
   * getDifferences returns an array containing the fields and the differences
   * between the returned ENROL response and the currently stored Person record.
   *
   * @returns {Object[]} - The differences between ENROL and a Person.
   */
  getDifferences(result) {
    let person = this.person;
    let differences = [];
    if (
      !isEmpty(result.firstName) &&
      this.firstNames !== result.firstName.trim()
    ) {
      differences.push({
        field: 'First name(s)',
        linced: this.firstNames,
        enrol: result.firstName,
      });
    }
    if (
      !isEmpty(result.lastName) &&
      this.lastName.trim() !== result.lastName.trim()
    ) {
      differences.push({
        field: 'Last name',
        linced: this.lastName,
        enrol: result.lastName,
      });
    }
    if (!isEmpty(result.birthdate) && person.dob !== result.birthdate) {
      differences.push({
        field: 'Date of birth',
        linced: person.dob,
        enrol: result.birthdate,
      });
    }
    let moeGender = person.get('fields.nzMOE:gender.value');
    if (!isEmpty(result.gender) && moeGender !== result.gender) {
      differences.push({
        field: 'Gender',
        linced: moeGender,
        enrol: result.gender,
      });
    }
    if (isEmpty(result.schoolName) && !isEmpty(result.startDate)) {
      // Enrolled at this school, but with a different start date
      differences.push({
        field: 'Start date',
        linced: person.get('fields.nzMOE:firstAttendance.value'),
        enrol: result.startDate,
      });
    }

    return differences;
  }

  @observes('person.id')
  idChanged() {
    this.setProperties({
      status: null,
      identifier: null,
    });
    if (this.entityType !== 'application') {
      return this.checkStatus();
    }
  }

  // isDirty computes whether the incoming person record has dirty attributes
  // to disable the Get/Check NSN button if unsaved changes are present.
  @alias('person.hasDirtyAttributes')
  isDirty;

  @computed('person.fields.{core:firstNameLegal.value,core:middleNames.value}')
  get firstNames() {
    let fields = this.person.fields;
    let firstNames =
      fields.get('core:firstNameLegal.value').trim() +
      ' ' +
      (fields.get('core:middleNames.value') ?? '').trim();
    return firstNames.trim();
  }

  @computed('person.fields.core:lastNameLegal.value')
  get lastName() {
    return this.person.fields.get('core:lastNameLegal.value').trim();
  }

  @action
  makeRequest() {
    let promise = Promise.resolve();
    if (!isEmpty(this.identifier)) {
      promise = this.archiveIdentifier();
    }
    return promise.then(() => {
      let fields = this.person.fields;
      let nsn = this.isValid ? this.person.jurisdictionalId : ''; // Don't send known-invalid NSNs.
      let search = {
        requesterUsername: this.session.user.username,
        providerId: this.session.school.jurisdictionalId,
        birthDate: fields.get('core:dob.value'),
        firstName: this.firstNames,
        lastName: this.lastName,
        gender: fields.get('nzMOE:gender.value'),
        nsn,
      };
      switch (this.entityType) {
        case 'application':
          search.applicationId = this.person.id;
          break;
        default:
          search.personId = this.person.id;
          break;
      }

      let status = this.store.createRecord('enrolments/status', {
        schoolId: this.session.school,
        requesterId: this.session.person,
        jurisdiction: Jurisdiction.NZ_MOE, // TODO: Make this dynamic when we add other jurisdictions
        resource: Resource.IDENTIFIERS,
        nzMoe: [search],
      });
      this.set('status', status);
      this.set('identifier', null);
      return status.save();
    });
  }

  @observes('status', 'identifier')
  pollStatus() {
    if (!isEmpty(this.status)) {
      if (isEmpty(this.identifier)) {
        // We have a Status (request), but not yet an Identifier (response).
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
      } else if (!isEmpty(this.pollTimer)) {
        // We've got an Identifier; cancel any pending status poll.
        cancel(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }

  /**
   * resetDetailsDialog resets required detail dialog parameters back to
   * defaults.
   */
  resetDetailsDialog() {
    this.detailDialogOpen = false;
    this.isConfirming = false;
    this.isRejecting = false;
    this.selectedResult = null;
  }

  /**
   * setField sets a dynamic field value in the Application record, creating it
   * if necessary.
   * setField assumes that the datatype is string, as that's all that is
   * necessary in this situation.
   *
   * @param key - The key of the field to set.
   * @param value - The new value of the field to set.
   */
  setField(key, value) {
    let field = this.get(`person.fields.${key}`);
    if (isEmpty(field)) {
      this.person.addField(key, 1);
      field = this.get(`person.fields.${key}`);
    }
    field.set('value', value);
  }

  updateStatus() {
    if (isEmpty(this.status)) {
      // May have been triggered during the process of archiving an Identifier,
      // in which case the Status may disappear.
      return;
    }
    this.status.reload().then((status) => {
      let params = this.baseFilterParams();
      params.statusId = status.id;
      switch (status.status) {
        case State.READY:
          // We don't know whether this Status request was an individual or
          // bulk request.
          // Fetching a list of Identifiers matching the Person and Status
          // should return the correct record either way.
          this.store.query('enrolments/identifier', params).then((records) => {
            this.set('identifier', records[0]);
          });
          break;

        case State.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          break;
      }
    });
  }

  /**
   * getDifferences calculates the differences between the returned ENROL
   * Record and the Person Record.
   *
   * @returns {*}
   */
  @action
  handleGetDifferences(result) {
    return this.getDifferences(result);
  }

  /**
   * openDetailDialog opens the GetCheckNSN dialog.
   */
  @action
  openDetailDialog() {
    this.detailDialogOpen = true;
  }

  /**
   * closeDetailDialog closes the GetCheckNSN dialog and optionally archives
   * the processed ENROL GetCheckNSN response.
   */
  @action
  closeDetailDialog(archive) {
    this.resetDetailsDialog();
    if (archive) {
      this.archiveIdentifier();
    }
  }

  /**
   * selectResult confirms merging the ENROL differences into the learner
   * record.
   *
   * @param {Object} result - The result returned from ENROL.
   */
  @action
  selectResult(result) {
    this.selectedResult = result;
    this.isConfirming = true;
  }

  /**
   * rejectResult moves to the rejection confirmation dialog.
   */
  @action
  rejectResult() {
    // If the user has decided to reject at the selection phase, make sure
    // that we eject out of the confirmation dialog.
    this.isConfirming = false;
    // Move to the rejection panel.
    this.isRejecting = true;
  }

  /**
   * confirmRejection removes the Student's NSN, Jurisdictional ID and then
   * archives the ENROL Identifier request enabling the user to perform a
   * different ENROL request.
   */
  @action
  confirmRejection() {
    this.isRejecting = false;
    this.resetDetailsDialog();

    let person = this.person;
    this.setField('nzMOE:nsn', '');
    person.set('jurisdictionalId', '');

    person.save().then(() => {
      // Archive the Identifier record as it's now been processed.
      this.archiveIdentifier();
    });
  }

  /**
   * acceptResult merges the ENROL differences into the learner record.
   * now performs an nsn conflict check first
   *
   * @param {Object} result - The result returned from ENROL.
   */
  @action
  acceptResult(result) {
    let person = this.person;

    if (
      this.entityType !== 'application' &&
      person.jurisdictionalId !== result.nsn
    ) {
      return this.students
        .searchByJurisdictionalId(person, result.nsn)
        .then((searchResponse) => {
          if (!isEmpty(searchResponse.searchResult)) {
            // Ensure we set the jurisdictional ID to the new value before we go to the conflict merge process.
            this.originalValue = person.jurisdictionalId;
            this._acceptResult(result, true);
            this.searchResponse = searchResponse;
          } else {
            return this._acceptResult(result, false);
          }
        });
    }

    return this._acceptResult(result, false);
  }

  _acceptResult(result, skipNSN) {
    let person = this.person;

    if (
      !isEmpty(result.firstName) &&
      this.firstNames !== result.firstName.trim()
    ) {
      // Push existing first name into preferred first name if currently blank
      if (isEmpty(this.get('person.fields.core:firstNamePreferred.value'))) {
        this.setField('core:firstNamePreferred', person.firstName);
      }
      let names = result.firstName.trim().split(' ');
      this.setField('core:firstNameLegal', names.shift());
      this.setField('core:middleNames', names.join(' '));
    }

    if (
      !isEmpty(result.lastName) &&
      this.lastName.trim() !== result.lastName.trim()
    ) {
      // Push existing last name into preferred last name if currently blank
      if (isEmpty(this.get('person.fields.core:lastNamePreferred.value'))) {
        this.setField('core:lastNamePreferred', person.lastName);
      }
      this.setField('core:lastNameLegal', result.lastName.trim());
    }

    if (person.jurisdictionalId !== result.nsn) {
      if (!skipNSN) {
        this.setField('nzMOE:nsn', result.nsn);
      }
      this.person.set('jurisdictionalId', result.nsn);
    }

    if (!isEmpty(result.birthdate) && person.dob !== result.birthdate) {
      this.setField('core:dob', result.birthdate);
    }

    if (
      !isEmpty(result.gender) &&
      person.get('fields.nzMOE:gender.value') !== result.gender
    ) {
      this.setField('nzMOE:gender', result.gender);
    }

    if (isEmpty(result.schoolName) && !isEmpty(result.startDate)) {
      // Enrolled at this school, but with a different start date
      this.setField('nzMOE:firstAttendance', result.startDate);
    }

    // Save changes - unless it's an Application to Enrol.
    let maybeSave = Promise.resolve();
    if (this.entityType !== 'application' && !skipNSN) {
      maybeSave = person.save();
    }

    return maybeSave
      .then(() => {
        // Once the person record has been saved, archive the Identifier record
        // as it's now been processed.
        this.archiveIdentifier();
      })
      .finally(() => {
        this.resetDetailsDialog();
      });
  }
}
